import { Button, Carousel } from 'antd';
import React, { useMemo, useRef } from 'react';
import { PlusOutlined } from '@ant-design/icons';

import { usePrevious } from '../../utils/hooks';
import { Container, Slide } from './CarouselFormGroup.style';

interface CarouselFormGroupProps extends Pick<React.ComponentProps<typeof Carousel>, 'effect'> {
    label?: string;
    addItem?(): void;
    addItemText?: string;
    hideAdd?: boolean;
    dotPosition?: 'top' | 'bottom';
    mode?: 'dark' | 'light';
}

const CarouselFormGroup = ({
    label,
    addItem,
    children,
    hideAdd,
    addItemText = 'Add',
    effect = 'fade',
    dotPosition = 'top',
    mode = 'dark',
}: React.PropsWithChildren<CarouselFormGroupProps>) => {
    const carouselRef = useRef<any>(null);
    const total = React.Children.count(children);
    const prevTotal = usePrevious(total);

    const addButtonRender = useMemo(
        () =>
            addItem &&
            !hideAdd && (
                <div style={{ gridColumn: '3/6', textAlign: 'right' }}>
                    <Button type='primary' icon={<PlusOutlined />} onClick={addItem}>
                        {addItemText}
                    </Button>
                </div>
            ),
        [hideAdd, addItem, addItemText]
    );

    const hasHeader = Boolean(label || addButtonRender);
    const containerClass = [dotPosition, total > 1 ? 'show-dots' : '', mode === 'dark' ? 'is-dark' : ''].filter(Boolean).join(' ');

    return (
        <>
            {hasHeader && (
                <div style={{ display: 'grid', gridColumn: '1 / 6', alignItems: 'center' }}>
                    {label && (
                        <div style={{ gridColumn: '1/3', fontSize: 20 }}>
                            <strong>{label}</strong>
                        </div>
                    )}
                    {addButtonRender}
                </div>
            )}
            <Container className={containerClass} style={{ display: total > 0 ? '' : 'none' }}>
                <Carousel
                    key={`carousel-${total}`}
                    ref={carouselRef}
                    initialSlide={prevTotal === undefined ? 0 : total - 1}
                    effect={effect}
                    dotPosition={dotPosition}
                    infinite={false}
                    waitForAnimate
                >
                    {React.Children.map(children, (child, index) => (
                        <div key={index}>
                            <Slide padded={mode === 'dark'}>{child}</Slide>
                        </div>
                    ))}
                </Carousel>
            </Container>
        </>
    );
};

export default React.memo(CarouselFormGroup);
